import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Building2, Search, ChevronRight } from "lucide-react";

function StatusPill({ status }) {
  const map = {
    active:    "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    trial:     "bg-amber-500/20 text-amber-300 border-amber-500/40",
    suspended: "bg-rose-500/20 text-rose-300 border-rose-500/40",
    churned:   "bg-slate-500/20 text-slate-300 border-slate-500/40",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest border ${map[status] || map.churned}`}>
      {status || "unknown"}
    </span>
  );
}

export default function PlatformOrgs() {
  const { api } = usePlatformAuth();
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orgs").then((r) => setOrgs(r.data)).finally(() => setLoading(false));
  }, [api]);

  const filtered = orgs.filter((o) => {
    if (!q) return true;
    const hay = `${o.name} ${o.slug} ${o.id}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="p-6 space-y-4" data-testid="platform-orgs">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Customers</div>
        <h1 className="text-2xl font-heading font-semibold">Organisations</h1>
        <p className="text-xs text-slate-500 mt-1">Manage customer organisations, plans, seat limits and account status.</p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="orgs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, slug or ID"
            className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-900 border border-slate-800 focus:border-emerald-500 focus:outline-none text-sm"
          />
        </div>
        <Link to="/platform-admin/onboarding" data-testid="orgs-new-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
          <Building2 className="w-3.5 h-3.5" /> Start onboarding
        </Link>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="orgs-table">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold border-b border-slate-800">
            <tr>
              <th className="text-left px-3 py-2">Organisation</th>
              <th className="text-left px-3 py-2">ID</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Sites</th>
              <th className="text-right px-3 py-2">Users</th>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-left px-3 py-2">Trial ends</th>
              <th className="text-right px-3 py-2 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-8 text-center text-slate-500 text-xs">Loading organisations…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-slate-500 text-xs" data-testid="orgs-empty">No organisations match your search.</td></tr>
            ) : filtered.map((o) => (
              <tr key={o.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`org-row-${o.id}`}>
                <td className="px-3 py-2 font-medium">{o.name}</td>
                <td className="px-3 py-2 text-slate-400 font-mono text-[11px]">{o.id}</td>
                <td className="px-3 py-2"><StatusPill status={o.status} /></td>
                <td className="px-3 py-2 text-slate-300 text-[12px]">{o.plan_id || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{o.site_count ?? 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">{o.user_count ?? 0}</td>
                <td className="px-3 py-2 text-slate-400 text-[11px]">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-slate-400 text-[11px]">{(o.trial_ends_at || "—").slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/platform-admin/organisations/${o.id}`} data-testid={`org-open-${o.id}`}
                        className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs font-semibold">
                    Open <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
